import { useEffect, useRef, useState } from "react";
import { Scissors, Loader2, Flag, FlagOff, Play } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import HlsPlayer from "@/components/HlsPlayer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipBumpers } from "@/components/ClipBumpers";
import { ClipEnding } from "@/components/ClipEnding";
import { ClipFormat, CropOverlay } from "@/components/ClipFormat";

const NO_ENDING = { type: "none", duration: 8, message: "", lines: "" };

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(2, "0"), ss = String(sec).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

const parse = (v) => {
  const parts = String(v).trim().split(":").map(Number);
  if (parts.some((n) => Number.isNaN(n))) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
};

function TimeField({ label, value, onChange, onMark, testid, Icon }) {
  const [text, setText] = useState(fmt(value));
  useEffect(() => setText(fmt(value)), [value]);
  return (
    <div className="space-y-1.5 flex-1 min-w-0">
      <Label className="text-gray-300 text-xs">{label}</Label>
      <div className="flex gap-2">
        <Input data-testid={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)}
          onBlur={() => { const n = parse(text); if (n !== null) onChange(n); else setText(fmt(value)); }}
          className="bg-gray-950 border-gray-800 font-mono text-sm focus-visible:ring-amber-500/40" />
        <button data-testid={`${testid}-mark`} type="button" onClick={onMark} title="Use the current player position"
          className="h-9 px-2.5 shrink-0 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1 transition-colors">
          <Icon className="h-3.5 w-3.5" /> Mark
        </button>
      </div>
    </div>
  );
}

export function ClipDialog({ recording, onClose, onCreated }) {
  const video = useRef(null);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(Math.min(60, recording?.duration_seconds || 60));
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [intro, setIntro] = useState("");
  const [outro, setOutro] = useState("");
  const [ending, setEnding] = useState(NO_ENDING);
  const [format, setFormat] = useState("wide");
  const [cropX, setCropX] = useState(0.5);
  const total = recording?.duration_seconds || 0;
  const len = end - start;

  useEffect(() => {
    if (!recording) return;
    setStart(0); setEnd(Math.min(60, recording.duration_seconds || 60));
    setTitle(`${recording.title} (clip)`);
    setIntro(""); setOutro(""); setEnding(NO_ENDING); setFormat("wide"); setCropX(0.5);
  }, [recording]);

  const now = () => video.current?.currentTime || 0;
  const preview = () => { if (video.current) { video.current.currentTime = start; video.current.play().catch(() => {}); } };

  const save = async () => {
    if (len < 1) return toast.error("The end point must be after the start point");
    const lines = ending.lines.split("\n");
    if (ending.type === "credits" && !lines.some((l) => l.trim())) return toast.error("Add at least one credit line");
    setSaving(true);
    try {
      await api.post(`/recordings/${recording.id}/clips`, { start, end, title, intro_id: intro || null, outro_id: outro || null, ending: { ...ending, lines }, format, crop_x: cropX });
      toast.success("Clip is being created. It'll appear in Uploaded videos shortly.");
      onCreated?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the clip"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!recording} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} data-testid="clip-dialog" className="bg-card border-gray-800 max-w-3xl max-h-[92vh] overflow-y-auto block space-y-4">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2"><Scissors className="h-5 w-5 text-amber-400" /> Clip editor</DialogTitle>
        </DialogHeader>
        <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
          {recording && <HlsPlayer key={recording.id} src={recording.playback_url} poster={recording.thumbnail_url} controls mediaRef={video} />}
          {format === "vertical" && <CropOverlay cropX={cropX} />}
        </div>
        <ClipFormat format={format} setFormat={setFormat} cropX={cropX} setCropX={setCropX} />
        <p className="text-xs text-gray-500">Play the video and press <span className="text-gray-300">Mark</span> to set each point, or type times like 1:05:30.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <TimeField label="Start" value={start} onChange={setStart} onMark={() => setStart(now())} testid="clip-start" Icon={Flag} />
          <TimeField label="End" value={end} onChange={setEnd} onMark={() => setEnd(now())} testid="clip-end" Icon={FlagOff} />
        </div>
        <div className="flex items-center justify-between text-xs">
          <span data-testid="clip-length" className={len < 1 || (total && end > total + 1) ? "text-red-400" : "text-gray-400"}>
            Clip length: <span className="font-mono text-white">{len > 0 ? fmt(len) : "—"}</span>{total ? <> of {fmt(total)}</> : null}
          </span>
          <button data-testid="clip-preview" type="button" onClick={preview} className="text-amber-400 hover:underline inline-flex items-center gap-1"><Play className="h-3.5 w-3.5" /> Play from start point</button>
        </div>
        <div className="space-y-1.5">
          <Label className="text-gray-300 text-xs">Clip name</Label>
          <Input data-testid="clip-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className="bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40" />
        </div>
        <div className="border-t border-gray-800 pt-4 space-y-5">
          <ClipBumpers ownerId={recording?.owner_id} intro={intro} outro={outro} setIntro={setIntro} setOutro={setOutro} />
          <ClipEnding ending={ending} setEnding={setEnding} />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button data-testid="clip-cancel" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-800">Cancel</button>
          <button data-testid="clip-save" onClick={save} disabled={saving || len < 1} className="px-4 py-2 rounded-lg text-sm font-semibold gold-gradient text-black inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} Save clip
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
